import { useState } from 'react';
import type { Tenant } from '../../../identity/api';
import { Button, Dialog, Switch } from '@nyce/admin-kit/ui';
import { client } from '../../../identity/session';
import { ErrorBox, Loading, useAsync } from '../../../components/common';
import { TenantFields, toCreate, useTenantForm } from './TenantForm';

/**
 * "New as copy": a new tenant that starts from another one's settings, claims and roles.
 *
 * AUTH has no copy endpoint, so this is POST /tenants followed by the two bare-array replaces. Service
 * keys and local users are deliberately left behind — a key is a secret that belongs to one tenant, and
 * local users carry password hashes the API never hands out.
 */
export function CopyTenantDialog({
  source,
  onCreated,
  onClose,
}: {
  source: Tenant;
  /** `warning` is set when the tenant was created but a claim/role copy failed. */
  onCreated: (tenant: Tenant, warning?: string) => void;
  onClose: () => void;
}) {
  // The row carries every tenant field, but the list DTO need not carry claims/roles; GET /tenants/{id} does.
  const full = useAsync(() => client.tenants.get(source.id), [source.id]);
  const claims = full.data?.claims ?? [];
  const roles = full.data?.roles ?? [];
  const form = useTenantForm({
    name: `${source.name} (copy)`,
    identityProvider: source.identityProvider,
    identityProviderUrl: source.identityProviderUrl,
    customerId: source.customerId,
  });
  const [withClaims, setWithClaims] = useState(true);
  const [withRoles, setWithRoles] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    let created: Tenant;
    try {
      created = await client.tenants.create(toCreate(form.value));
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
      return;
    }
    // Past this point the tenant exists: a failed copy is reported, never retried as another create.
    const failed: string[] = [];
    if (withClaims && claims.length) {
      try {
        await client.tenants.claims.replace(
          created.id,
          claims.map(({ type, value }) => ({ type, value }))
        );
      } catch (e) {
        failed.push(`claims (${(e as Error).message})`);
      }
    }
    if (withRoles && roles.length) {
      try {
        await client.tenants.roles.replace(
          created.id,
          roles.map(({ name, scopes }) => ({ name, scopes }))
        );
      } catch (e) {
        failed.push(`roles (${(e as Error).message})`);
      }
    }
    setBusy(false);
    onCreated(
      created,
      failed.length ? `Created ${created.name}, but could not copy ${failed.join(' and ')}` : undefined
    );
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`New tenant as copy of ${source.name}`}
      maxWidth={720}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!form.valid || full.loading}>
            Create copy
          </Button>
        </>
      }
    >
      <ErrorBox error={error ?? full.error} />
      <Loading loading={full.loading} />
      <TenantFields value={form.value} onChange={form.setValue} />
      <div className="nyk-inline" style={{ gap: 18, marginTop: 14 }}>
        <Switch
          checked={withClaims && !!claims.length}
          onChange={setWithClaims}
          disabled={!claims.length}
          label={claims.length ? `Copy ${claims.length} claim(s)` : 'No claims to copy'}
        />
        <Switch
          checked={withRoles && !!roles.length}
          onChange={setWithRoles}
          disabled={!roles.length}
          label={roles.length ? `Copy ${roles.length} role(s)` : 'No roles to copy'}
        />
      </div>
      <p className="nyk-card-intro" style={{ marginTop: 14 }}>
        The copy starts Active. Service keys and local users are not copied — issue a key and create users on
        the new tenant page.
      </p>
    </Dialog>
  );
}
